import type {
  RevenuePartner,
  RevenuePayoutItem,
  RevenuePayoutRecord,
  Tax1099ReadinessRow,
} from "../../types/revenueOps";

export const TAX_REPORTING_THRESHOLDS: Record<number, number> = {
  2026: 2000,
};

export const HISTORICAL_TAX_REPORTING_THRESHOLD = 600;

export type Tax1099ReadinessInput = {
  tax_year: number;
  partners: RevenuePartner[];
  payouts: RevenuePayoutRecord[];
  payout_items: RevenuePayoutItem[];
};

export function getTaxReportingThreshold(taxYear: number): number {
  return TAX_REPORTING_THRESHOLDS[taxYear] || (taxYear < 2026 ? HISTORICAL_TAX_REPORTING_THRESHOLD : 2000);
}

function isPreparedStatus(status: RevenuePayoutRecord["status"]): boolean {
  return ["draft", "pending_review", "approved", "partially_paid"].includes(status);
}

function profileStatus(partner: RevenuePartner): NonNullable<RevenuePartner["tax_profile_status"]> {
  return partner.tax_profile_status || "not_started";
}

export function deriveTax1099ReadinessRows(input: Tax1099ReadinessInput): Tax1099ReadinessRow[] {
  const payoutById = new Map(input.payouts.map((payout) => [payout.id, payout]));
  const totals = new Map<string, { paid: number; prepared: number }>();
  input.payout_items.forEach((item) => {
    const payout = payoutById.get(item.payout_batch_id);
    if (!payout || payout.status === "canceled") return;
    const current = totals.get(item.partner_id) || { paid: 0, prepared: 0 };
    if (payout.status === "paid") current.paid += Number(item.amount) || 0;
    if (isPreparedStatus(payout.status) && payout.status !== "partially_paid") current.prepared += Number(item.amount) || 0;
    totals.set(item.partner_id, current);
  });

  const threshold = getTaxReportingThreshold(input.tax_year);
  return input.partners.map((partner) => {
    const totalsForPartner = totals.get(partner.id) || { paid: 0, prepared: 0 };
    const status = profileStatus(partner);
    const blockers: string[] = [];
    if (totalsForPartner.paid > 0 || totalsForPartner.prepared > 0) {
      if (status !== "exempt_or_not_required" && !partner.w9_received_at) blockers.push("Missing W-9");
      if (status !== "exempt_or_not_required" && !partner.tax_legal_name) blockers.push("Missing tax legal name");
      if (status !== "exempt_or_not_required" && !partner.tax_address_summary) blockers.push("Missing tax address");
    }
    if (totalsForPartner.prepared > 0 && totalsForPartner.paid === 0) blockers.push("No paid payout confirmation workflow exists yet");
    if (status === "exempt_or_not_required" && !partner.tax_notes) blockers.push("CPA note is required for exempt/not-required status");

    let readiness_status: Tax1099ReadinessRow["readiness_status"];
    if (status === "exempt_or_not_required" && !partner.tax_notes) readiness_status = "needs_cpa_review";
    else if (blockers.includes("Missing W-9")) readiness_status = "missing_w9";
    else if (blockers.includes("Missing tax legal name")) readiness_status = "missing_tax_legal_name";
    else if (blockers.includes("Missing tax address")) readiness_status = "missing_tax_address";
    else if (blockers.includes("No paid payout confirmation workflow exists yet")) readiness_status = "missing_paid_payout_confirmation";
    else if (totalsForPartner.paid < threshold) readiness_status = "below_reporting_threshold";
    else readiness_status = "ready_for_cpa_review";

    return {
      tax_year: input.tax_year,
      partner_id: partner.id,
      partner_name: partner.display_name,
      partner_type: partner.partner_type,
      tax_profile_status: status,
      tax_legal_name: partner.tax_legal_name,
      tax_classification: partner.tax_classification,
      tax_tin_last4: partner.tax_tin_last4,
      tax_address_summary: partner.tax_address_summary,
      w9_received_at: partner.w9_received_at,
      paid_payout_total: totalsForPartner.paid,
      prepared_payout_total: totalsForPartner.prepared,
      reportable_candidate: totalsForPartner.paid >= threshold,
      readiness_status,
      blockers,
      notes: partner.tax_notes,
    };
  });
}

export function summarizeTax1099Rows(rows: Tax1099ReadinessRow[]) {
  return {
    partners: rows.length,
    paid_total: rows.reduce((sum, row) => sum + row.paid_payout_total, 0),
    prepared_total: rows.reduce((sum, row) => sum + row.prepared_payout_total, 0),
    reportable_candidates: rows.filter((row) => row.reportable_candidate).length,
    blocked: rows.filter((row) => row.readiness_status !== "below_reporting_threshold" && row.readiness_status !== "ready_for_cpa_review").length,
  };
}

function csvCell(value: unknown): string {
  const text = value === undefined || value === null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildTax1099ReadinessCsv(rows: Tax1099ReadinessRow[]): string {
  const columns = ["tax_year", "partner_id", "partner_name", "partner_type", "tax_profile_status", "tax_legal_name", "tax_classification", "tax_tin_last4", "tax_address_summary", "w9_received_at", "paid_payout_total", "prepared_payout_total", "reportable_candidate", "readiness_status", "blockers", "notes"];
  const lines = rows.map((row) => [
    row.tax_year,
    row.partner_id,
    row.partner_name,
    row.partner_type,
    row.tax_profile_status,
    row.tax_legal_name,
    row.tax_classification,
    row.tax_tin_last4,
    row.tax_address_summary,
    row.w9_received_at,
    row.paid_payout_total.toFixed(2),
    row.prepared_payout_total.toFixed(2),
    row.reportable_candidate,
    row.readiness_status,
    row.blockers.join("; "),
    row.notes,
  ].map(csvCell).join(","));
  return [columns.join(","), ...lines].join("\n");
}
